import { HandPointUp } from "@gravity-ui/icons";
import {
  Button,
  Flex,
  Icon,
  SegmentedRadioGroup,
  Text,
} from "@gravity-ui/uikit";
import {
  ProductSourceDto,
  ProductSourceDtoSourceEnum,
} from "definitions/product-storage";
import { useMemo } from "react";
import { DewuLogo, GoatLogo, PoizonComLogo } from "src/components";

import { useProductPriceSourceTranslate } from "./hooks";
import css from "./ProductPriceSource.module.scss";

interface ProductPriceSourceProps {
  source?: ProductSourceDto[];
  platform: ProductSourceDtoSourceEnum | null;
  setPlatform: (platform: null | ProductSourceDtoSourceEnum) => void;
  reload: () => void;
  isDesktop: boolean;
}

export const ProductPriceSource = ({
  source,
  platform,
  setPlatform,
  reload,
  isDesktop,
}: ProductPriceSourceProps) => {
  const translations = useProductPriceSourceTranslate();

  const availableSources = useMemo(() => {
    return new Set(source?.map((s) => s.source));
  }, [source]);

  const availablePlatforms = useMemo(
    () =>
      [
        {
          value: ProductSourceDtoSourceEnum.Poizon,
          label: translations.poizon,
          icon: PoizonComLogo,
        },
        {
          value: ProductSourceDtoSourceEnum.Dewu,
          label: translations.dewu,
          icon: DewuLogo,
        },
        {
          value: ProductSourceDtoSourceEnum.Goat,
          label: translations.goat,
          icon: GoatLogo,
        },
      ].filter((p) => availableSources.has(p.value)),
    [availableSources, translations],
  );

  const setRadioGroupPlatform = (value: string) => {
    if (isProductSource(value)) {
      setPlatform(value);
    }
  };

  const togglePlatform = (value: ProductSourceDtoSourceEnum) => {
    setPlatform(value === platform ? null : value);
  };

  return (
    <Flex direction="column" gap="2">
      <Flex
        direction="row"
        justifyContent={isDesktop ? "space-between" : undefined}
        alignContent="center"
        gap="3"
      >
        <Text variant={isDesktop ? "subheader-2" : "subheader-3"}>
          {translations.title_price}
        </Text>
        <Button size="s" onClick={reload}>
          {translations.update_price}
        </Button>
      </Flex>

      {isDesktop ? (
        <Flex direction="column" gap="2">
          {availablePlatforms.map((p) => (
            <Button
              key={p.value}
              width="max"
              selected={p.value === platform}
              size="l"
              onClick={() => togglePlatform(p.value)}
            >
              <Icon data={p.icon} size={20} />
              {p.label}
            </Button>
          ))}
        </Flex>
      ) : (
        <>
          {availablePlatforms.length > 0 && (
            <Flex>
              <SegmentedRadioGroup
                size="l"
                value={platform ?? undefined}
                onUpdate={setRadioGroupPlatform}
              >
                {availablePlatforms.map((p) => (
                  <SegmentedRadioGroup.Option key={p.value} value={p.value}>
                    <Flex direction="row" gap="2" alignItems="center">
                      <Icon data={p.icon} size={15} />
                      {p.label}
                    </Flex>
                  </SegmentedRadioGroup.Option>
                ))}
              </SegmentedRadioGroup>
            </Flex>
          )}
          {!platform && availablePlatforms.length > 0 && (
            <Flex alignItems="center" gap="2" className={css.promptHint}>
              <Icon data={HandPointUp} size={20} />
              <Text>{translations.select_platform_hint}</Text>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};

const isProductSource = (value: string): value is ProductSourceDtoSourceEnum =>
  Object.values(ProductSourceDtoSourceEnum).includes(
    value as ProductSourceDtoSourceEnum,
  );
